import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { FileText, Download, Loader2, RefreshCw, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { useBranch } from '../contexts/BranchContext';
import { formatDate } from '../utils/dateUtils';
import { creditDebitReportApiService, CreditDebitReportRow } from '../services/creditDebitReportApi';

const CreditDebitReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();

  const { todayDate, yesterdayDate } = useMemo(() => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const toDateInputValue = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      todayDate: toDateInputValue(today),
      yesterdayDate: toDateInputValue(yesterday),
    };
  }, []);

  const [rows, setRows] = useState<CreditDebitReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [reportDateFrom, setReportDateFrom] = useState(yesterdayDate);
  const [reportDateTo, setReportDateTo] = useState(todayDate);
  const [appliedReportDateFrom, setAppliedReportDateFrom] = useState(yesterdayDate);
  const [appliedReportDateTo, setAppliedReportDateTo] = useState(todayDate);

  const loadReport = async (from = appliedReportDateFrom, to = appliedReportDateTo) => {
    if (!selectedBranch) {
      setRows([]);
      return;
    }

    try {
      setLoading(true);
      const response = await creditDebitReportApiService.getCreditDebitReport(
        selectedBranch,
        from || undefined,
        to || undefined
      );
      setRows(response.success && Array.isArray(response.data) ? response.data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBranch) {
      void loadReport();
    } else {
      setRows([]);
    }
  }, [selectedBranch]);

  const handleApplyFilters = async () => {
    if (reportDateFrom && reportDateTo && reportDateFrom > reportDateTo) {
      toast.error('Date from cannot be after date to.');
      return;
    }
    setAppliedReportDateFrom(reportDateFrom);
    setAppliedReportDateTo(reportDateTo);
    await loadReport(reportDateFrom, reportDateTo);
  };

  const handleClearFilters = async () => {
    setReportDateFrom('');
    setReportDateTo('');
    setAppliedReportDateFrom('');
    setAppliedReportDateTo('');
    await loadReport('', '');
  };

  const formatNumber = (value?: number | null) => {
    if (value == null || Number.isNaN(value)) return '0.00';
    return Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getComputedGrandTotal = (row: CreditDebitReportRow) =>
    Number(row.amount || 0) +
    Number(row.cgstAmount || 0) +
    Number(row.sgstAmount || 0) +
    Number(row.igstAmount || 0) +
    Number(row.tcsAmount || 0) +
    Number(row.otherCharges || 0);

  const reportSummary = useMemo(() => {
    return rows.reduce(
      (acc: { amount: number; totalTaxAmount: number; grandTotal: number }, row) => {
        acc.amount += Number(row.amount || 0);
        acc.totalTaxAmount += Number(row.totalTaxAmount || 0);
        acc.grandTotal += getComputedGrandTotal(row);
        return acc;
      },
      { amount: 0, totalTaxAmount: 0, grandTotal: 0 }
    );
  }, [rows]);

  const exportToCSV = () => {
    const headers = [
      'Note No',
      'Note Date',
      'Sales Invoice No',
      'Sales Invoice Date',
      'Voucher Type',
      'Party Name',
      'Customer Address',
      'GSTIN',
      'PAN',
      'Item Name',
      'HSN Code',
      'Billed Quantity',
      'Rate',
      'Unit',
      'Amount',
      'CGST Rate',
      'CGST Amount',
      'SGST Rate',
      'SGST Amount',
      'IGST Rate',
      'IGST Amount',
      'TCS Rate',
      'TCS Amount',
      'Total Tax Amount',
      'Other Charges',
      'Grand Total',
      'Additional Remarks',
    ];

    const csvRows = rows.map((row) => [
      row.noteNo ?? '',
      row.noteDate ?? '',
      row.salesInvoiceNo ?? '',
      row.salesInvoiceDate ?? '',
      row.voucherType ?? '',
      row.partyName ?? '',
      row.customerAddress ?? '',
      row.gstin ?? '',
      row.pan ?? '',
      row.itemName ?? '',
      row.hsnCode ?? '',
      row.billedQuantity ?? '',
      row.rate ?? '',
      row.unit ?? '',
      row.amount ?? '',
      row.cgstRate ?? '',
      row.cgstAmount ?? '',
      row.sgstRate ?? '',
      row.sgstAmount ?? '',
      row.igstRate ?? '',
      row.igstAmount ?? '',
      row.tcsRate ?? '',
      row.tcsAmount ?? '',
      row.totalTaxAmount ?? '',
      row.otherCharges ?? '',
      getComputedGrandTotal(row),
      row.additionalRemarks ?? '',
    ]);

    const csvContent = [headers, ...csvRows]
      .map((row) => row.map((field) => `"${String(field ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `credit-debit-report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-xl">Credit / Debit Report</CardTitle>
                  <p className="text-sm text-primary-100">Report by note date range</p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/invoices')}
                className="border-white/30 bg-white text-primary-700 hover:bg-primary-50"
              >
                Back to Invoices
              </Button>
            </div>
          </CardHeader>
        </Card>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="flex items-center gap-2 text-primary-800">
              <Calendar className="h-5 w-5 text-primary-700" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div className="space-y-2">
                <Label htmlFor="reportDateFrom">Date From</Label>
                <Input
                  id="reportDateFrom"
                  type="date"
                  value={reportDateFrom}
                  onChange={(e) => setReportDateFrom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reportDateTo">Date To</Label>
                <Input
                  id="reportDateTo"
                  type="date"
                  value={reportDateTo}
                  onChange={(e) => setReportDateTo(e.target.value)}
                />
              </div>
              <Button onClick={() => void handleApplyFilters()} className="bg-primary-600 hover:bg-primary-700 text-white">
                <RefreshCw className="h-4 w-4 mr-2" />
                Apply Filters
              </Button>
              <Button variant="outline" onClick={() => void handleClearFilters()}>
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-primary-200 shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Amount</div>
              <div className="text-2xl font-semibold text-primary-800">₹{formatNumber(reportSummary.amount)}</div>
            </CardContent>
          </Card>
          <Card className="border-primary-200 shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Total Tax Amount</div>
              <div className="text-2xl font-semibold text-primary-800">₹{formatNumber(reportSummary.totalTaxAmount)}</div>
            </CardContent>
          </Card>
          <Card className="border-primary-200 shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Grand Total</div>
              <div className="text-2xl font-semibold text-primary-800">₹{formatNumber(reportSummary.grandTotal)}</div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <div className="flex items-center justify-between gap-4">
              <CardTitle className="text-lg text-primary-800">Report Data</CardTitle>
              <Button
                onClick={exportToCSV}
                variant="outline"
                className="border-primary-200 text-primary-700 hover:bg-primary-50"
                disabled={rows.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {!selectedBranch ? (
              <div className="text-center py-8 text-gray-500">Please select a branch to view the report.</div>
            ) : loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No credit/debit report data found for the selected filters.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <TableHead>Note No</TableHead>
                      <TableHead>Note Date</TableHead>
                      <TableHead>Sales Invoice No</TableHead>
                      <TableHead>Sales Invoice Date</TableHead>
                      <TableHead>Voucher Type</TableHead>
                      <TableHead>Party Name</TableHead>
                      <TableHead>Customer Address</TableHead>
                      <TableHead>GSTIN</TableHead>
                      <TableHead>PAN</TableHead>
                      <TableHead>Item Name</TableHead>
                      <TableHead>HSN Code</TableHead>
                      <TableHead className="text-right">Billed Quantity</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">CGST Rate</TableHead>
                      <TableHead className="text-right">CGST Amount</TableHead>
                      <TableHead className="text-right">SGST Rate</TableHead>
                      <TableHead className="text-right">SGST Amount</TableHead>
                      <TableHead className="text-right">IGST Rate</TableHead>
                      <TableHead className="text-right">IGST Amount</TableHead>
                      <TableHead className="text-right">TCS Rate</TableHead>
                      <TableHead className="text-right">TCS Amount</TableHead>
                      <TableHead className="text-right">Total Tax Amount</TableHead>
                      <TableHead className="text-right">Other Charges</TableHead>
                      <TableHead className="text-right">Grand Total</TableHead>
                      <TableHead>Additional Remarks</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row, index) => {
                      const noteDateRaw = row.noteDate;
                      const salesInvDateRaw = row.salesInvoiceDate;
                      return (
                      <TableRow key={`${row.noteNo || 'row'}-${index}`} className="hover:bg-primary-50/40">
                        <TableCell>{row.noteNo || 'N/A'}</TableCell>
                        <TableCell>{noteDateRaw ? formatDate(noteDateRaw) : 'N/A'}</TableCell>
                        <TableCell>{row.salesInvoiceNo ?? 'N/A'}</TableCell>
                        <TableCell>{salesInvDateRaw ? formatDate(salesInvDateRaw) : 'N/A'}</TableCell>
                        <TableCell>{row.voucherType || 'N/A'}</TableCell>
                        <TableCell>{row.partyName || 'N/A'}</TableCell>
                        <TableCell>{row.customerAddress || 'N/A'}</TableCell>
                        <TableCell>{row.gstin || 'N/A'}</TableCell>
                        <TableCell>{row.pan || 'N/A'}</TableCell>
                        <TableCell>{row.itemName || 'N/A'}</TableCell>
                        <TableCell>{row.hsnCode || '—'}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.billedQuantity)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.rate)}</TableCell>
                        <TableCell>{row.unit || 'N/A'}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.amount)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.cgstRate)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.cgstAmount)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.sgstRate)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.sgstAmount)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.igstRate)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.igstAmount)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.tcsRate)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.tcsAmount)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.totalTaxAmount)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.otherCharges)}</TableCell>
                        <TableCell className="text-right">{formatNumber(getComputedGrandTotal(row))}</TableCell>
                        <TableCell>{row.additionalRemarks || ''}</TableCell>
                      </TableRow>
                    );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreditDebitReportPage;

